// Author: Sunil Jain
// File: e1/e1c/src/components/Navbar.js
// Updated: 2025-09-11 - To use a generic icon as the fallback logo.

import React, { useState, useMemo, useCallback } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Badge from '@mui/material/Badge';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import NotificationList from './NotificationList';
import Skeleton from '@mui/material/Skeleton';

// Import MUI Icons
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PeopleIcon from '@mui/icons-material/People';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LogoutIcon from '@mui/icons-material/Logout';
import BusinessIcon from '@mui/icons-material/Business';
import NotificationsIcon from '@mui/icons-material/Notifications';

export const drawerWidth = 240;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8001';

export default function Navbar(props) {
    const { window: windowProp } = props;
    const [mobileOpen, setMobileOpen] = useState(false);
    const queryClient = useQueryClient();
    const [notificationMenuAnchor, setNotificationMenuAnchor] = useState(null);

    const {
        user: userProfile,
        logout,
        memberships,
        selectedTenant,
        selectTenant,
        isAuthenticated,
        authFetch,
        isLoading: isAuthLoading,
    } = useAuth();
    
    // START OF FIX: Fetch branding data using useQuery to ensure it's always fresh
    const { data: tenantBranding, isLoading: isBrandingLoading } = useQuery({
        queryKey: ['tenant-branding', selectedTenant?.tenant_id],
        queryFn: () => authFetch('/branding/'),
        enabled: !!selectedTenant,
        staleTime: 1000 * 60 * 5, // Cache for 5 minutes, consistent with BrandingManager
    });
    // END OF FIX

    const { data: notifications } = useQuery({
        queryKey: ['notifications', selectedTenant?.tenant_id],
        queryFn: () => authFetch('/api/notifications/'),
        enabled: !!isAuthenticated && !!selectedTenant,
        refetchInterval: 60000,
    });
    
    const unreadCount = useMemo(() => {
        if (!notifications) return 0;
        return notifications.filter(n => !n.is_read).length;
    }, [notifications]);

    const markAsReadMutation = useMutation({
        mutationFn: () => authFetch('/api/notifications/mark-all-as-read', { method: 'POST' }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        },
    });

    const handleNotificationMenuOpen = (event) => {
        if (unreadCount > 0) {
            markAsReadMutation.mutate();
        }
        setNotificationMenuAnchor(event.currentTarget);
    };

    const handleNotificationMenuClose = () => {
        setNotificationMenuAnchor(null);
    };

    const location = useLocation();
    const userFirstName = userProfile?.first_name || userProfile?.email || '';

    const handleDrawerToggle = useCallback(() => {
        setMobileOpen(prev => !prev);
    }, []);

    const handleTenantChange = useCallback((event) => {
        const newTenantId = event.target.value;
        const selectedMembershipObject = memberships.find(m => m.tenant_id === newTenantId);
        if (selectedMembershipObject) {
            selectTenant(selectedMembershipObject);
        }
    }, [memberships, selectTenant]);

    const STAFF_ROLES = useMemo(() => [
        'physician_qhp', 'clinical_staff', 'tenant_admin', 'platform_admin', 'super_user'
    ], []);
    
    const ADMIN_ROLES = useMemo(() => [
        'tenant_admin', 'physician_qhp', 'platform_admin', 'super_user'
    ], []);
    
    const selectedRole = selectedTenant?.role || null;

    const allNavItems = useMemo(() => [
        { text: 'Dashboard', icon: <FlightTakeoffIcon />, path: '/dashboard', roles: STAFF_ROLES },
        { text: 'Content Studio', icon: <LibraryBooksIcon />, path: '/content-studio', roles: STAFF_ROLES },
        { text: 'Profile', icon: <AccountCircleIcon />, path: '/profile', roles: STAFF_ROLES },
        { text: 'Tenant Settings', icon: <AdminPanelSettingsIcon />, path: '/admin/tenant-settings', roles: ADMIN_ROLES },
    ], [ADMIN_ROLES, STAFF_ROLES]);

    const visibleNavItems = useMemo(() => {
        if (!selectedRole) return [];
        return allNavItems.filter(item => item.roles.includes(selectedRole));
    }, [selectedRole, allNavItems]);

    const drawerContent = (
        <Box onClick={mobileOpen ? handleDrawerToggle : undefined} sx={{ textAlign: 'left' }}>
            <Toolbar sx={{ justifyContent: 'flex-start', pl: 2, minHeight: { xs: 56, sm: 64 } }} />
            <Divider />
            <List>
                {visibleNavItems.map((item) => (
                    <ListItem key={item.text} disablePadding>
                        <ListItemButton component={RouterLink} to={item.path} selected={location.pathname.startsWith(item.path)} sx={{ '&.Mui-selected': { backgroundColor: 'action.selected', fontWeight: 'fontWeightBold' }, '&:hover': { backgroundColor: 'action.hover' } }}>
                            <ListItemIcon sx={{ minWidth: '40px', color: 'inherit' }}>{item.icon}</ListItemIcon>
                            <ListItemText primary={item.text} />
                        </ListItemButton>
                    </ListItem>
                ))}
                <ListItem key="LogoutDrawer" disablePadding sx={{ display: { sm: 'none' } }}>
                     <ListItemButton onClick={logout}><ListItemIcon sx={{ minWidth: '40px', color: 'inherit' }}><LogoutIcon /></ListItemIcon><ListItemText primary="Logout" /></ListItemButton>
                 </ListItem>
            </List>
        </Box>
    );

    const container = windowProp !== undefined ? () => windowProp().document.body : undefined;

    // MODIFIED: Returns null on fallback to trigger icon component rendering.
    const logoUrl = useMemo(() => {
        // START OF FIX: Use the snake_case property from the API response
        const path = tenantBranding?.logo_url;
        // END OF FIX
        if (!path) {
            return null;
        }
        if (path.startsWith('/images/')) {
            return `${process.env.PUBLIC_URL}${path}`;
        }
        try {
            return new URL(path, API_BASE_URL).href;
        } catch (e) {
            logger.error("Error constructing logo URL, using fallback.", e);
            return null;
        }
    }, [tenantBranding]);
    
    // START OF FIX: Update loading logic to account for the new branding query
    const isLogoLoading = isAuthLoading || (!!selectedTenant && isBrandingLoading);
    // END OF FIX

    return (
        <>
            <AppBar position="fixed" sx={{ width: { sm: isAuthenticated ? `calc(100% - ${drawerWidth}px)` : '100%' }, ml: { sm: isAuthenticated ? `${drawerWidth}px` : 0 }, zIndex: (theme) => theme.zIndex.drawer + 1, transition: (theme) => theme.transitions.create(['margin', 'width'], { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.leavingScreen }) }} elevation={1}>
                <Toolbar>
                    {isAuthenticated && <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2, display: { sm: 'none' } }}><MenuIcon /></IconButton>}
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                        {isLogoLoading ? (
                            <Skeleton variant="rectangular" width={150} height={40} sx={{ bgcolor: 'grey.700', mr: 2 }} />
                        ) : (
                            // MODIFIED: Render the image if a URL exists, otherwise render the generic BusinessIcon.
                            logoUrl ? (
                                <Box 
                                    component="img"
                                    src={logoUrl}
                                    alt={selectedTenant?.tenant_name ? `${selectedTenant.tenant_name} Logo` : "Company Logo"}
                                    sx={{ 
                                        height: { xs: '52px', sm: '64px' }, // UPDATED: Increased size to fill navbar (52px mobile, 64px desktop)
                                        maxWidth: '200px', 
                                        marginRight: '20px', 
                                        objectFit: 'contain' 
                                    }}
                                />
                            ) : (
                                <BusinessIcon sx={{ fontSize: '40px', mr: 2 }} />
                            )
                        )}
                        
                        <Typography
                            variant="h6"
                            component="a" // CHANGED: Use a standard 'a' tag for external links
                            href="https://www.medsai.us" // CHANGED: Point to the absolute external URL
                            target="_blank" // ADDED: Open in a new tab
                            rel="noopener noreferrer" // ADDED: Security for external links
                            sx={{ color: 'inherit', textDecoration: 'none', fontWeight: 500 }}
                        >
                            MedsAi<sup>™</sup> {/* ADDED: Trademark symbol */}
                        </Typography>

                    </Box>

                    {isAuthenticated && userProfile && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" noWrap sx={{ mr: 2, display: { xs: 'none', md: 'block' } }}>Welcome, {userFirstName}!</Typography>
                            {memberships && memberships.length > 1 && selectedTenant && (
                                <FormControl variant="standard" sx={{ m: 1, minWidth: 120, mr: 2 }}>
                                    <Select labelId="tenant-select-label" id="tenant-select" value={selectedTenant.tenant_id} onChange={handleTenantChange} disableUnderline sx={{ color: 'inherit', '& .MuiSelect-icon': { color: 'inherit' }, '& .MuiSelect-select': { paddingRight: '24px', backgroundColor: 'transparent' } }}>
                                        {memberships.map((membership) => (
                                            <MenuItem key={membership.tenant_id} value={membership.tenant_id}>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}><BusinessIcon sx={{ mr: 1, fontSize: '1rem', opacity: 0.8 }} /><Typography variant="body2">{membership.tenant_name || `Tenant ${membership.tenant_id.substring(0, 6)}`} ({membership.role})</Typography></Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                            {memberships && memberships.length === 1 && selectedTenant && (<Typography variant="body2" sx={{ mr: 2, display: { xs: 'none', md: 'block' }, border: '1px solid rgba(255,255,255,0.3)', padding: '2px 8px', borderRadius: '4px' }}>{selectedTenant.tenant_name} ({selectedTenant.role})</Typography>)}
                            <Tooltip title="Notifications">
                                <IconButton color="inherit" aria-label="Notifications" id="notifications-button" aria-controls={notificationMenuAnchor ? 'notifications-menu' : undefined} aria-haspopup="true" aria-expanded={notificationMenuAnchor ? 'true' : undefined} onClick={handleNotificationMenuOpen}>
                                    <Badge badgeContent={unreadCount} color="error"><NotificationsIcon /></Badge>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Logout">
                                <IconButton color="inherit" onClick={logout} sx={{ display: { xs: 'none', sm: 'inline-flex' }, ml: 1 }} aria-label="Logout"><LogoutIcon /></IconButton>
                            </Tooltip>
                        </Box>
                    )}
                </Toolbar>
            </AppBar>
            {isAuthenticated && (
                 <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }} aria-label="main navigation">
                    <Drawer container={container} variant="temporary" open={mobileOpen} onClose={handleDrawerToggle} ModalProps={{ keepMounted: true }} sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth } }}>{drawerContent}</Drawer>
                    <Drawer variant="permanent" sx={{ display: { xs: 'none', sm: 'block' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: 'none' } }} open>{drawerContent}</Drawer>
                </Box>
            )}
             <NotificationList
                notifications={notifications}
                anchorEl={notificationMenuAnchor}
                open={Boolean(notificationMenuAnchor)}
                onClose={handleNotificationMenuClose}
            />
        </>
    );
}